/**
 * Site Charts Page
 *
 * Interactive time-series charts for Battery, Solar, and EV asset monitoring
 * Real-time updates via WebSocket
 */

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, TrendingUp } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { BatterySOCChart } from '../components/charts/BatterySOCChart';
import { SolarGenerationChart } from '../components/charts/SolarGenerationChart';
import { EVSessionsChart } from '../components/charts/EVSessionsChart';
import { useKPISubscription } from '../hooks/useKPISubscription';
import api from '../services/api';
import { useEffect } from 'react';
import { socketService } from '../services/socket';
import { tenantName, tenantLogo } from '../config/tenant';

export function SiteCharts() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Connect to WebSocket on mount
  useEffect(() => {
    try {
      socketService.connect();
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
    }

    return () => {
      // Don't disconnect on unmount - keep connection alive
      // socketService.disconnect();
    };
  }, []);

  // Subscribe to real-time updates
  useKPISubscription(id || '', !!id);

  // Fetch site details from projects endpoint (which has proper structure)
  const { data: siteData, isLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const response = await api.get(`/v1/projects/${id}`);
      return response.data.data.site;
    },
    enabled: !!id
  });

  if (isLoading || !siteData) {
    return (
      <div className="min-h-screen bg-ems-bg p-8">
        <div className="text-ems-text-secondary">Loading site charts...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ems-bg">
      {/* Header */}
      <header className="border-b border-ems-border sticky top-0 z-50 bg-ems-bg-card">
        <div className="w-full px-8 py-5">
          <div className="flex items-center justify-between">
            {/* Left: Logo + Back Navigation */}
            <div className="flex items-center gap-6">
              <img
                src={tenantLogo}
                alt={tenantName}
                className="w-10 h-10 rounded-lg cursor-pointer object-contain"
                onClick={() => navigate('/dashboard')}
              />
              <button
                onClick={() => navigate(`/project/${id}`)}
                className="flex items-center gap-2 text-ems-text-secondary hover:text-ems-green transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Project Summary</span>
              </button>
            </div>

            {/* Center: Title */}
            <div className="flex items-center gap-3">
              <TrendingUp className="w-6 h-6 text-ems-green" />
              <h1 className="text-xl font-bold text-ems-text-primary">
                Analytics Dashboard
              </h1>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.location.reload()}
                className="p-2.5 rounded-lg transition-all hover:bg-ems-bg-card-hover border border-ems-border"
              >
                <RefreshCw className="w-5 h-5 text-ems-green" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-8 py-8">
        {/* Site Header */}
        <div className="rounded-2xl bg-ems-bg-card border border-ems-border p-6 mb-8">
          <h2 className="text-xl font-bold text-ems-text-primary mb-2">
            {siteData.site_name}
          </h2>
          <p className="text-sm text-ems-text-muted">
            Real-time performance analytics with live data updates
          </p>
        </div>

        {/* Charts Grid */}
        <div className="space-y-6">
          {/* Battery Storage Charts */}
          {siteData.asset_type === 'battery_storage' && (
            <>
              <BatterySOCChart siteId={id!} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Additional battery charts can be added here */}
              </div>
            </>
          )}

          {/* Solar PV Charts */}
          {siteData.asset_type === 'solar_pv' && (
            <>
              <SolarGenerationChart siteId={id!} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Additional solar charts can be added here */}
              </div>
            </>
          )}

          {/* EV Charging Charts */}
          {siteData.asset_type === 'ev_charging' && (
            <>
              <EVSessionsChart siteId={id!} />
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Additional EV charts can be added here */}
              </div>
            </>
          )}

          {/* Multi-Asset Site - Show All Charts */}
          {!siteData.asset_type && (
            <>
              <h3 className="text-lg font-semibold text-ems-text-primary mt-8 mb-4">
                Battery Storage
              </h3>
              <BatterySOCChart siteId={id!} />

              <h3 className="text-lg font-semibold text-ems-text-primary mt-8 mb-4">
                Solar Generation
              </h3>
              <SolarGenerationChart siteId={id!} />

              <h3 className="text-lg font-semibold text-ems-text-primary mt-8 mb-4">
                EV Charging
              </h3>
              <EVSessionsChart siteId={id!} />
            </>
          )}
        </div>

        {/* Real-Time Indicator */}
        <div className="mt-8 flex justify-center">
          <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-ems-bg-card border border-ems-border">
            <div className="w-2 h-2 rounded-full bg-ems-green animate-ems-pulse" />
            <span className="text-sm text-ems-text-secondary">
              Live data updates enabled
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}
